import type { Timeline, CreateTimelineInput } from '@peles/shared';

export interface PortableTimeline {
  _version: 1;
  _type: 'timeline';
  templateName: string | null;
  title: string | null;
  description: string | null;
  rainPlanNotes: string | null;
  sections: Array<{
    name: string;
    nameHe: string | null;
    sortOrder: number;
    entries: Array<{
      time: string | null;
      title: string;
      location: string | null;
      vendorNote: string | null;
      notes: string | null;
      sortOrder: number;
    }>;
  }>;
}

export function exportTimeline(timeline: Timeline): void {
  const portable: PortableTimeline = {
    _version: 1,
    _type: 'timeline',
    templateName: timeline.templateName,
    title: timeline.title,
    description: timeline.description,
    rainPlanNotes: timeline.rainPlanNotes,
    sections: timeline.sections.map((s) => ({
      name: s.name,
      nameHe: s.nameHe,
      sortOrder: s.sortOrder,
      entries: s.entries.map((e) => ({
        time: e.time,
        title: e.title,
        location: e.location,
        vendorNote: e.vendorNote,
        notes: e.notes,
        sortOrder: e.sortOrder,
      })),
    })),
  };

  const json = JSON.stringify(portable, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeName = (timeline.templateName || timeline.title || 'timeline')
    .replace(/[^a-z0-9]/gi, '_')
    .toLowerCase();
  a.download = `${safeName}.timeline.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function parsePortableTimeline(json: string): PortableTimeline {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('Invalid JSON file.');
  }
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    (parsed as Record<string, unknown>)._type !== 'timeline' ||
    !Array.isArray((parsed as Record<string, unknown>).sections)
  ) {
    throw new Error('File is not a valid timeline export.');
  }
  return parsed as PortableTimeline;
}

export function portableToCreateInput(
  portable: PortableTimeline,
  opts: { eventId?: string; isTemplate?: boolean },
): CreateTimelineInput {
  return {
    eventId: opts.eventId,
    isTemplate: opts.isTemplate,
    templateName: portable.templateName ?? undefined,
    title: portable.title ?? undefined,
    description: portable.description ?? undefined,
    rainPlanNotes: portable.rainPlanNotes ?? undefined,
    sections: portable.sections.map((s) => ({
      name: s.name,
      nameHe: s.nameHe ?? undefined,
      sortOrder: s.sortOrder,
      entries: s.entries.map((e) => ({
        time: e.time ?? undefined,
        title: e.title,
        location: e.location ?? undefined,
        vendorNote: e.vendorNote ?? undefined,
        notes: e.notes ?? undefined,
        sortOrder: e.sortOrder,
      })),
    })),
  };
}
